'use client';
import React, { useState } from 'react';
import { toast } from 'sonner';
import { Toaster } from 'sonner';
import DashboardKPIs from './DashboardKPIs';
import LeadInboxTable from './LeadInboxTable';
import LeadDetailModal from './LeadDetailModal';
import DashboardCharts from './DashboardCharts';
import { mockLeads, Lead } from './mockLeads';

export default function DashboardContent() {
  const [leads, setLeads] = useState<Lead[]>(mockLeads);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleStatusChange = (id: string, newStatus: Lead['status']) => {
    // TODO: PATCH /api/leads/:id with { status: newStatus } — update Supabase leads table
    setLeads((prev) => prev.map((l) => l.id === id ? { ...l, status: newStatus } : l));
    toast.success(`Lead marked as ${newStatus}`);
  };

  const handleBulkAction = (action: 'Contacted' | 'Rejected') => {
    // TODO: PATCH /api/leads/bulk with { ids: [...], status: action }
    setLeads((prev) =>
      prev.map((l) => selectedIds.has(l.id) ? { ...l, status: action } : l)
    );
    toast.success(`${selectedIds.size} leads marked as ${action}`);
    setSelectedIds(new Set());
  };

  const handleToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) setSelectedIds(new Set(leads.map((l) => l.id)));
    else setSelectedIds(new Set());
  };

  return (
    <>
      <Toaster position="bottom-right" theme="dark" />
      {/* Page header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-800 text-white">Lead Inbox</h1>
          <p className="text-[#666] text-sm mt-0.5">
            {leads.filter((l) => l.status === 'New').length} new leads · Last synced just now
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/20 rounded-full">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse-slow" />
            <span className="text-xs text-emerald-400 font-600">Live</span>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <DashboardKPIs leads={leads} />

      {/* Charts */}
      <div className="mt-6">
        <DashboardCharts leads={leads} />
      </div>

      {/* Lead Table */}
      <div className="mt-6">
        <LeadInboxTable
          leads={leads}
          selectedIds={selectedIds}
          onToggleSelect={handleToggleSelect}
          onSelectAll={handleSelectAll}
          onStatusChange={handleStatusChange}
          onViewLead={setSelectedLead}
          onBulkAction={handleBulkAction}
        />
      </div>

      {/* Detail Modal */}
      {selectedLead && (
        <LeadDetailModal
          lead={selectedLead}
          onClose={() => setSelectedLead(null)}
          onStatusChange={(status) => {
            handleStatusChange(selectedLead.id, status);
            setSelectedLead((prev) => prev ? { ...prev, status } : null);
          }}
        />
      )}
    </>
  );
}